import type { User } from '@/types/UserData';
import { KycStatus } from '@/shared/Kyc/base';

const currentMessageIndex = ref<number>(0);

export const useKyc = () => {
  const { t } = useTexts();
  const { user } = storeToRefs(useUserDataStore());
  const { kycId, kycSource } = storeToRefs(useKycStore());

  const { isFeatureFromApiEnabled, getStringValueFromApi } =
    useFeatureFlagsNew();

  const isKycIntegrationEnabled = computed<boolean>(() => {
    return isFeatureFromApiEnabled('kyc_integration_active');
  });

  const kycIntegrationName = computed<string>(() => {
    return getStringValueFromApi('kyc_integration_name');
  });

  const kycStatusData = computed<User['latestKyc']>(() => {
    return user.value?.latestKyc;
  });

  const kycOperator = computed<Kyc.Operator | undefined>(() => {
    return user.value?.latestKyc?.operator;
  });

  const isApproved = computed<boolean>(() => {
    // TODO: Remover quando o backend subir a pay2free
    if (typeof kycStatusData.value?.status === 'string') {
      return kycStatusData.value?.status === 'APPROVED';
    }

    return (
      kycStatusData.value?.status === KycStatus.APPROVED ||
      kycStatusData.value?.status === KycStatus.MANUAL_APPROVED
    );
  });

  const isLivenessPending = computed<boolean>(() => {
    const { isKycForcedRequest } = useUserData();

    if (isKycForcedRequest.value) {
      return isKycForcedRequest.value;
    }

    // TODO: Remover quando o backend subir a pay2free
    if (typeof kycStatusData.value?.status === 'string') {
      return kycStatusData.value?.status === 'LIVENESS_PENDING';
    }

    return kycStatusData.value?.status === KycStatus.LIVENESS_PENDING;
  });

  const isReproved = computed<boolean>(() => {
    // TODO: Remover quando o backend subir a pay2free
    if (typeof kycStatusData.value?.status === 'string') {
      return kycStatusData.value?.status === 'REPROVED';
    }

    return (
      kycStatusData.value?.status === KycStatus.REPROVED ||
      kycStatusData.value?.status === KycStatus.MANUAL_REPROVED
    );
  });

  const isNewUser = computed<boolean>(() => {
    return !kycStatusData.value?.status;
  });

  const isProcessing = computed<boolean>(() => {
    return !isApproved.value && !isReproved.value && !isNewUser.value;
  });

  const isFullOnboarding = computed<boolean>(() => {
    if (!kycStatusData.value) {
      return true;
    }

    const { operation_type } = kycStatusData.value;

    if (!operation_type) {
      return true;
    }

    if (operation_type === 'onboarding' && isApproved.value) {
      return false;
    }

    if (isLivenessPending.value) {
      return false;
    }

    return operation_type === 'onboarding';
  });

  const loadingMessages = ref<string[]>([
    t('verification.kyc.loading_messages.1'),
    t('verification.kyc.loading_messages.2'),
    t('verification.kyc.loading_messages.3'),
  ]);

  function nextLoadingMessage() {
    if (currentMessageIndex.value < loadingMessages.value.length - 1) {
      currentMessageIndex.value += 1;
    } else {
      currentMessageIndex.value = 0;
    }
  }

  const loadingMessage = computed<string>(() => {
    return loadingMessages.value[currentMessageIndex.value] || '';
  });

  return {
    isKycIntegrationEnabled,
    kycIntegrationName,
    KycStatus,
    kycOperator,
    isProcessing,
    isApproved,
    isLivenessPending,
    isReproved,
    isFullOnboarding,
    currentMessageIndex,
    nextLoadingMessage,
    loadingMessage,
    kycSource,
    kycId,
  };
};
